import { Box, Button, InputAdornment, makeStyles, TextField, Typography } from '@material-ui/core';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';
import ReplayIcon from '@material-ui/icons/Replay';
import ThumbDownAltOutlinedIcon from '@material-ui/icons/ThumbDownAltOutlined';
import ThumbUpAltOutlinedIcon from '@material-ui/icons/ThumbUpAltOutlined';
import React from 'react';

import {
  benchwiseSearchwise,
  benchwiseSearchwiseFeedbackDown,
  benchwiseSearchwiseFeedbackUp,
} from '../../../../core/api/benchmark';
import {
  useFilterDispatch,
  useFilterState,
} from '../../shared/FinancialDataContext/FinancialDataContext';
import { FinancialDataContextActionTypes } from '../../shared/FinancialDataContext/FinancialDataContext.types';
import { useSearchState } from '../../shared/SearchContext/SearchContext';
import { AssistHistoryItem, useAssistContext } from './CompaniesFilterAssistContext';

const useStyles = makeStyles((theme) => ({
  chatContainer: {
    overflowY: 'auto',
    marginBottom: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    flex: 1,
  },
  message: {
    padding: '10px 16px', // explicit pixel padding for reliable chip appearance
    borderRadius: 16,
    background: '#e3f2fd',
    fontSize: 12,
    alignSelf: 'flex-start',
    maxWidth: '90%',
    wordBreak: 'break-word',
    marginBottom: theme.spacing(1), // add margin between chips
    marginTop: theme.spacing(1),
    display: 'inline-block',
    position: 'relative',
    // Ensure child Typography also gets padding
    '& .MuiTypography-root': {
      padding: 0,
      fontSize: 12,
      lineHeight: 1.2,
    },
    '&:hover $messageActions': {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
  userMessage: {
    background: '#c8e6c9',
    alignSelf: 'flex-end',
    // Remove padding here, all padding is in .message
  },
  inputContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
    alignItems: 'center',
    position: 'relative',
  },
  input: {
    flex: 1,
    fontSize: 12,
    height: 32,
    '& .MuiInputBase-input': {
      padding: '6px 8px',
      fontSize: 12,
      height: 20,
    },
  },
  resetButton: {
    minWidth: 28,
    padding: 2,
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
    background: 'rgba(255,255,255,0.7)',
    borderRadius: 12,
    '&:hover': {
      background: theme.palette.action.hover,
      color: theme.palette.primary.main,
    },
  },
  button: {
    minWidth: 24,
    fontSize: 12,
    padding: 0,
    height: 28, // reduced height to fit input
    lineHeight: 1,
    boxShadow: 'none',
  },
  messageActions: {
    position: 'absolute',
    top: 4,
    right: 4,
    display: 'flex',
    gap: theme.spacing(1),
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 0.2s',
    zIndex: 2,
  },
  iconButton: {
    minWidth: 24,
    padding: 2,
    color: theme.palette.text.secondary,
    background: 'rgba(255,255,255,0.7)',
    borderRadius: 12,
    '&:hover': {
      background: theme.palette.action.hover,
      color: theme.palette.primary.main,
    },
  },
  feedbackInputBox: {
    position: 'absolute',
    top: 32,
    right: 0,
    background: '#fff',
    border: '1px solid #eee',
    borderRadius: 8,
    padding: theme.spacing(1),
    zIndex: 10,
    boxShadow: theme.shadows[2],
    minWidth: 300,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  feedbackInput: {
    flex: 1,
    fontSize: 12,
    padding: theme.spacing(0.3, 0.5),
  },
  feedbackButton: {
    minWidth: 32,
    padding: '4px 8px',
    fontSize: 12,
  },
  resizableContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    margin: `${theme.spacing(1)}px 0`,
    background: '#f9f9f9',
    border: '1px solid #eee',
    borderRadius: 8,
    padding: theme.spacing(1),
    boxSizing: 'border-box',
    position: 'relative',
  },
  splitter: {
    height: 4,
    background: '#ddd',
    cursor: 'row-resize',
    borderRadius: 2,
    margin: `${theme.spacing(0.5)}px 0`,
    transition: 'background 0.2s',
    position: 'relative',
    '&:hover': {
      background: theme.palette.primary.main,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: -2,
      left: 0,
      right: 0,
      height: 8,
    },
  },
  splitterActive: {
    background: theme.palette.primary.main,
  },
  processingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: '10px 16px',
    borderRadius: 16,
    background: '#e3f2fd',
    fontSize: 12,
    alignSelf: 'flex-start',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  processingDots: {
    display: 'flex',
    gap: 2,
    '& span': {
      width: 4,
      height: 4,
      borderRadius: '50%',
      background: theme.palette.text.secondary,
      animation: '$processing 1.4s infinite ease-in-out both',
      '&:nth-child(1)': { animationDelay: '-0.32s' },
      '&:nth-child(2)': { animationDelay: '-0.16s' },
    },
  },
  '@keyframes processing': {
    '0%, 80%, 100%': {
      transform: 'scale(0)',
    },
    '40%': {
      transform: 'scale(1)',
    },
  },
}));

export const CompaniesFilterAssist: React.FC = () => {
  const classes = useStyles();
  const { data, requestFiltersData, historySearch } = useFilterState();
  const { searchRequest } = useSearchState();
  const dispatch = useFilterDispatch();
  const {
    input,
    setInput,
    messages,
    setMessages,
    loading,
    setLoading,
    previousInstructions,
    setPreviousInstructions,
    history,
    setHistory,
  } = useAssistContext();

  const [feedbackOpenIdx, setFeedbackOpenIdx] = React.useState<number | null>(null);
  const [feedbackComment, setFeedbackComment] = React.useState('');
  const [feedbackLoading, setFeedbackLoading] = React.useState(false);
  const [containerHeight, setContainerHeight] = React.useState(240);
  const [isResizing, setIsResizing] = React.useState(false);
  const [showProcessing, setShowProcessing] = React.useState(false);
  const [isUserResized, setIsUserResized] = React.useState(false);

  const chatContainerRef = React.useRef<HTMLDivElement>(null);
  const resizeStartY = React.useRef<number>(0);
  const resizeStartHeight = React.useRef<number>(0);

  const calculateDynamicHeight = React.useCallback((messageCount: number): number => {
    const baseHeight = 80;
    const messageHeight = 45;
    const calculatedHeight = baseHeight + messageCount * messageHeight;
    return Math.min(calculatedHeight, 300); // Auto-grow up to 300px
  }, []);

  React.useEffect(() => {
    if (!isUserResized) {
      const newHeight = calculateDynamicHeight(messages.length + (showProcessing ? 1 : 0));
      setContainerHeight(newHeight);
    }
  }, [messages.length, showProcessing, isUserResized, calculateDynamicHeight, setContainerHeight]);

  const scrollToBottom = React.useCallback(() => {
    if (chatContainerRef.current) {
      const container = chatContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  React.useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleResizeStart = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsResizing(true);
    setIsUserResized(true); // Mark that user has manually resized
    resizeStartY.current = e.clientY;
    resizeStartHeight.current = containerHeight;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - resizeStartY.current;
      const newHeight = Math.max(120, Math.min(400, resizeStartHeight.current + deltaY));
      setContainerHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleReload = (uid?: string) => {
    if (!uid) return;
    const item = history.find((h) => h.uid === uid);
    if (item && item.response.filters) {
      dispatch({
        type: FinancialDataContextActionTypes.Set,
        payload: { filters: item.response.filters, historySearch: !!historySearch },
      });
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;
    // console.log('handleSend called requestFilters', data, requestFiltersData, appliedFilters);
    // console.log('Search request:', searchRequest);
    // console.log('Previous instructions:', previousInstructions);
    setMessages([...messages, { role: 'user', content: input }]);
    setLoading(true);
    setShowProcessing(true);
    try {
      const req = {
        filters: data || searchRequest?.filters || requestFiltersData || {},
        instructions: input,
        previousInstructions: previousInstructions.length > 0 ? previousInstructions : undefined,
      };
      const response = await benchwiseSearchwise(req);
      if (!response || !response.filters)
        throw new Error('Invalid response from benchwiseSearchwise');
      setMessages([
        ...messages,
        { role: 'user', content: input },
        { role: 'bot', content: response.summary || 'Filters updated.' },
      ]);
      setHistory([
        ...history,
        {
          instructions: input,
          response,
          uid: response.uid,
        },
      ]);
      dispatch({
        type: FinancialDataContextActionTypes.Set,
        payload: { historySearch: !!historySearch, filters: response.filters },
      });
      setPreviousInstructions([...previousInstructions, input]);
      setInput('');
    } catch (e) {
      setMessages([
        ...messages,
        { role: 'user', content: input },
        {
          role: 'bot',
          content:
            "I'm having trouble processing your request right now. Please try rephrasing your instructions or being more specific about what you'd like to filter.",
        },
      ]);
    } finally {
      setLoading(false);
      setShowProcessing(false);
    }
  };

  const handleResetChat = () => {
    setMessages([]);
    setHistory([]);
    setPreviousInstructions([]);
    setInput('');
    setIsUserResized(false); // Reset to auto-sizing when chat is cleared
  };

  const handleThumbUp = async (uid: string) => {
    setFeedbackLoading(true);
    try {
      await benchwiseSearchwiseFeedbackUp(uid);
      setHistory(
        history.map((h: AssistHistoryItem) => (h.uid === uid ? { ...h, feedbackSent: true } : h)),
      );
    } catch (e) {
      // Optionally show error
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleThumbDown = (idx: number) => {
    setFeedbackOpenIdx(idx);
    setFeedbackComment('');
  };

  const handleFeedbackSubmit = async (uid: string) => {
    if (!feedbackComment.trim()) return;
    setFeedbackLoading(true);
    try {
      await benchwiseSearchwiseFeedbackDown(uid, feedbackComment);
      setHistory(
        history.map((h: AssistHistoryItem) => (h.uid === uid ? { ...h, feedbackSent: true } : h)),
      );
      setFeedbackOpenIdx(null);
      setFeedbackComment('');
    } catch (e) {
      // Optionally show error
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleFeedbackCancel = () => {
    setFeedbackOpenIdx(null);
    setFeedbackComment('');
  };

  return (
    <Box
      className={classes.resizableContainer}
      style={{
        height: containerHeight,
        transition: isResizing ? 'none' : 'height 0.3s ease',
      }}
    >
      <div
        ref={chatContainerRef}
        className={classes.chatContainer}
        style={{ height: containerHeight - 80 }}
      >
        {messages.map((msg, idx) => {
          let historyItem: typeof history[0] | undefined;
          let chipUid: string | undefined;
          if (msg.role === 'bot') {
            historyItem = history.find((h) => h.response.summary === msg.content && h.uid);
            chipUid = historyItem?.uid as string | undefined;
          }
          const feedbackSent = historyItem?.feedbackSent;
          return (
            <Box
              key={idx}
              className={
                msg.role === 'bot' ? classes.message : `${classes.message} ${classes.userMessage}`
              }
              style={{ position: 'relative' }}
            >
              <Typography variant="caption">{msg.content}</Typography>
              {msg.role === 'bot' && typeof chipUid === 'string' && (
                <Box className={classes.messageActions}>
                  <Button
                    className={classes.iconButton}
                    size="small"
                    onClick={() => handleReload(chipUid)}
                    title="Reload filters"
                  >
                    <ReplayIcon fontSize="small" />
                  </Button>
                  {!feedbackSent && (
                    <>
                      <Button
                        className={classes.iconButton}
                        size="small"
                        title="Thumbs up"
                        disabled={feedbackLoading}
                        onClick={() => handleThumbUp(chipUid || '')}
                      >
                        <ThumbUpAltOutlinedIcon fontSize="small" />
                      </Button>
                      <Button
                        className={classes.iconButton}
                        size="small"
                        title="Thumbs down"
                        disabled={feedbackLoading}
                        onClick={() => handleThumbDown(idx)}
                      >
                        <ThumbDownAltOutlinedIcon fontSize="small" />
                      </Button>
                    </>
                  )}
                  {/* Feedback input dialog */}
                  {feedbackOpenIdx === idx && !feedbackSent && (
                    <ClickAwayListener onClickAway={handleFeedbackCancel}>
                      <Box className={classes.feedbackInputBox}>
                        <TextField
                          className={classes.feedbackInput}
                          value={feedbackComment}
                          onChange={(e) => setFeedbackComment(e.target.value)}
                          placeholder="Comment"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleFeedbackSubmit(chipUid!);
                            }
                            if (e.key === 'Escape') {
                              handleFeedbackCancel();
                            }
                          }}
                          disabled={feedbackLoading}
                          size="small"
                        />
                        <Button
                          className={classes.feedbackButton}
                          color="primary"
                          variant="contained"
                          size="small"
                          onClick={() => handleFeedbackSubmit(chipUid!)}
                          disabled={feedbackLoading || !feedbackComment.trim()}
                        >
                          Send
                        </Button>
                      </Box>
                    </ClickAwayListener>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
        {showProcessing && (
          <Box className={classes.processingIndicator}>
            <Typography variant="caption">Analyzing your question</Typography>
            <Box className={classes.processingDots}>
              <span />
              <span />
              <span />
            </Box>
          </Box>
        )}
      </div>
      <Box
        className={`${classes.splitter} ${isResizing ? classes.splitterActive : ''}`}
        onMouseDown={handleResizeStart}
      />
      <Box className={classes.inputContainer}>
        {/* Reset chat button at the left of input */}
        <Button
          className={classes.resetButton}
          size="small"
          onClick={handleResetChat}
          title="Reset chat"
        >
          <DeleteSweepIcon fontSize="small" />
        </Button>
        <TextField
          className={classes.input}
          variant="outlined"
          size="small"
          placeholder="How can I assist you with the search? Currently in preview mode..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !loading) handleSend();
          }}
          disabled={loading}
          inputProps={{ style: { fontSize: 12, padding: 4 } }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  className={classes.button}
                  variant="contained"
                  color="primary"
                  onClick={handleSend}
                  disabled={loading || !input.trim()}
                  style={{ minWidth: 24, height: 28, padding: 0, marginLeft: 4, boxShadow: 'none' }}
                >
                  <ArrowUpwardIcon style={{ fontSize: 18 }} />
                </Button>
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </Box>
  );
};
